import type { AppLocale } from './numerals'

/* ============================================================================
   plural.ts — the sentence for a count, in the form the language actually uses.

   ARABIC HAS FIVE PLURAL FORMS WHERE ENGLISH HAS TWO, and the difference is not a
   suffix: the NOUN changes. A single template with a number in it reads to an Arabic
   speaker the way "there is 3 things" reads to an English one.

   ── WHY THIS IS IN `@hk/core` AND NOT IN THREE PLACES ─────────────────────────

   It was in two. The guest form grew one in Sprint 20 (#58's sprint), the handset grew
   an identical one in Sprint 27 (#92), and W-127's "1 rooms cannot be given to anyone"
   would have been the THIRD — in a project whose most frequently repeated defect is a
   rule enforced in one of the two places that state it.

   The three callers differ only in which dictionary they read. That is not enough to
   justify three copies of `Intl.PluralRules`, three caches, and three chances for one
   of them to fall behind.

   ── WHAT IT DELIBERATELY DOES NOT DO ──────────────────────────────────────────

   IT DOES NOT FORMAT THE NUMBER. The caller passes it already formatted, because
   whether a figure is Eastern or Western digits is a decision about what KIND of number
   it is — a count localises, an identifier never does — and this function cannot tell
   them apart. `formatCount` is what knows.

   AND IT IS NOT AVAILABLE TO THE ERROR CATALOGUE, which is one message per code with no
   machinery at all. A sentence that has to live there is SHAPED to need no agreement
   instead: "A photograph is needed for {count} of the things you ticked."
   ========================================================================= */

/** The CLDR categories. English selects only `one` and `other`; Arabic uses all five. */
export type Plural = 'one' | 'two' | 'few' | 'many' | 'other'

const rules = new Map<string, Intl.PluralRules>()

/**
 * @param forms  every form the language might select — the English half deliberately
 *               repeats itself across `two`, `few` and `many` so both dictionaries hold
 *               the same keys and the guard that compares them stays useful.
 * @param formatted the count, ALREADY localised by `formatCount`.
 */
export function pluralise(
  forms: Record<Plural, string>, count: number, locale: AppLocale, formatted: string,
): string {
  const intlLocale = locale === 'ar' ? 'ar-IQ' : 'en-GB'
  let chooser = rules.get(intlLocale)
  if (!chooser) { chooser = new Intl.PluralRules(intlLocale); rules.set(intlLocale, chooser) }

  /* `select` can return "zero", which neither language needs for any sentence in this
     product — they are all drawn only when something is outstanding. Falling back to
     "other" keeps that true the day one of them is drawn at zero anyway. */
  const category = chooser.select(count) as Plural
  return (forms[category] ?? forms.other).replace('{count}', formatted)
}
